import React, { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import IconButton from "@cayuse/shared-react.components.icon-button";
import DragHandle from "../Internal/DragHandle";

import styles from "./styles.css";

const ItemComponent = ({
	item,
	index,
	itemType,
	onItemChange,
	deleteItem,
	addItem,
	moveItem,
	totalItems,
	children
}) => {
	const ref = useRef(null);

	const [ , drop ] = useDrop({
		accept: itemType,
		hover (item, monitor) {
			if (!ref.current) {
				return;
			}
			const dragIndex = item.index;
			const hoverIndex = index;
			// Don't replace items with themselves
			if (dragIndex === hoverIndex) {
				return;
			}
			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect();
			// Get vertical middle
			const hoverMiddleY =
              (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			// Determine mouse position
			const clientOffset = monitor.getClientOffset();
			// Get pixels to the top
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;
			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%
			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}
			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}
			// Time to actually perform the action
			moveItem(dragIndex, hoverIndex);
			// Note: we're mutating the monitor item here!
			// Generally it's better to avoid mutations,
			// but it's good here for the sake of performance
			// to avoid expensive index searches.
			item.index = hoverIndex;
		},
	});

	const [ { isDragging }, drag, preview ] = useDrag({
		item: { type: itemType, id: item.index, index },
		collect: monitor => ({
			isDragging: monitor.isDragging(),
		}),
	});

	drop(ref);

	return (
		<div ref={ref} key={`question-${index}`}>
			<div ref={preview} className={`${styles.optionContainer} ${isDragging ? styles.dragging : ""}`}>
				<div ref={drag}>
					<DragHandle/>
				</div>
				{children}
				<div className={`ui input ${styles.optionInputContainer}`}>
					<input
						aria-label={`Answer option ${index + 1}`}
						value={item.text || ""}
						onChange={event => onItemChange(index, event.target.value)}
					/>
				</div>
				{totalItems > 1 && (
					<IconButton
						aria-label={`Delete answer option ${index + 1}`}
						className={styles.deleteButton}
						icon="trash"
						onClick={() => deleteItem(index)}
						tertiary
					/>
				)}
				<IconButton
					className={`${styles.addButton} ${totalItems - 1 !== index ? styles.addButtonHidden : ""}`}
					icon="plus"
					onClick={addItem}
					aria-label="Add new answer option"
					tertiary
				/>
			</div>
		</div>
	);
};

export default ItemComponent;
